import { mergeAdditionalRolodexFilters } from 'features/drilldown/components/table/mergeAdditionalRolodexFilters'
import { useFocusItems } from 'features/drilldown/data/DrilldownFilterContext'
import { useFilteredEntryQuery } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { usePaginatedQuery } from 'features/fde/helpers/usePaginatedQuery'
import { getNiiReportConfig } from 'features/libra/stepComponents/niiReport/niiReportApi'
import {
  type LibraTicket,
  parseLibraTicketEntry,
  useLibraWorkflows,
} from 'features/libra/ticket/libraTicketsApi'
import { getLatestVersions } from 'features/libra/ticket/ticketVersioning'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import { useMemo } from 'react'
import {
  type GeckoEntry,
  RolodexEntryType,
  type RolodexHistogramFilter,
} from 'types'

/**
 * One page big enough to hold any realistic campaign, matching
 * `useNiiCampaignRoi` — the export needs the whole scope, not a page of it.
 */
const LOAD_ALL_PAGINATION = { pageSize: 10_000, currentPage: 1 }

/**
 * By default only finished analyses are bulk-exported; a half-filled report
 * PDF isn't something anyone sends on. Callers can opt out with
 * `includeIncomplete` (the spreadsheet export does, for partial tickets).
 *
 * Deliberately NOT reusing `NII_RESULTS_FILTERS` from the results table:
 * that set also pins the ticket group's title to one hardcoded campaign
 * (`NII_CAMPAIGN_TITLE`), which would silently make this panel useless for
 * every other campaign and org. "Is this an NII ticket?" is instead answered
 * below by `getNiiReportConfig` — the workflow either has a valid NII Report
 * step or it doesn't, which is the real property rather than a proxy for it.
 */
const NII_COMPLETE_TICKET_FILTERS: ReadonlyArray<RolodexHistogramFilter> = [
  {
    type: RolodexEntryType.LIBRA_TICKET,
    key: 'workflowComplete',
    op: 'truthy',
  },
]

function parseTickets(entries: ReadonlyArray<GeckoEntry>): LibraTicket[] {
  return entries
    .filter((entry) => entry.type === RolodexEntryType.LIBRA_TICKET)
    .map(parseLibraTicketEntry)
    .filter((ticket): ticket is LibraTicket => ticket !== undefined)
}

/** Focused rows narrow the export the same way they narrow other panels. */
function focusItemsToFilters(
  focusItems: ReturnType<typeof useFocusItems>[0]
): ReadonlyArray<RolodexHistogramFilter> {
  if (!focusItems) return []
  return [...focusItems.entries()].flatMap(([type, ids]) =>
    ids.length
      ? [
          {
            type,
            field: 'id',
            op: 'in',
            value: [...ids],
          } satisfies RolodexHistogramFilter,
        ]
      : []
  )
}

export interface NiiPdfExportScope {
  /**
   * Latest version per assessment, restricted to tickets whose workflow
   * actually has a valid NII Report step — i.e. exactly the tickets a PDF
   * can be produced for.
   */
  tickets: ReadonlyArray<LibraTicket>
  workflowMap: ReadonlyMap<string, LibraWorkflow>
  /**
   * In-scope tickets that were dropped for having no usable NII Report step.
   * Surfaced so the panel can say why a visible row isn't exportable rather
   * than silently omitting it.
   */
  ineligibleCount: number
  isLoading: boolean
}

/**
 * The NII tickets in the current drilldown scope, ready to hand to
 * `useBulkNiiPdfExport`.
 *
 * Scope comes from the Explorer itself — cascading filters from panels above
 * plus any focused rows — so this panel deliberately has no ticket picker of
 * its own; narrowing the export is done with the filter panel, the same way
 * every other panel is narrowed.
 *
 * Campaign-agnostic: the only additions to the drilldown scope are "the
 * workflow is complete" (unless `includeIncomplete`) and "the workflow has an
 * NII Report step", so this works for any NII campaign in any org that has
 * the panel enabled.
 */
export function useNiiPdfExportScope({
  includeIncomplete = false,
}: { includeIncomplete?: boolean } = {}): NiiPdfExportScope {
  const { query, isLoading: queryLoading } = useFilteredEntryQuery(
    RolodexEntryType.LIBRA_TICKET
  )
  const [focusItems] = useFocusItems()

  const focusFilters = useMemo(
    () => focusItemsToFilters(focusItems),
    [focusItems]
  )

  const ticketQuery = useMemo(
    () =>
      mergeAdditionalRolodexFilters(
        mergeAdditionalRolodexFilters(
          query,
          includeIncomplete ? [] : NII_COMPLETE_TICKET_FILTERS
        ),
        focusFilters
      ),
    [focusFilters, includeIncomplete, query]
  )

  const { query: ticketEntryQuery, isLoading: ticketPlanLoading } =
    usePaginatedQuery(
      ticketQuery,
      LOAD_ALL_PAGINATION,
      RolodexEntryType.LIBRA_TICKET
    )

  const { entries: ticketEntries, isLoading: ticketsLoading } =
    useRolodexEntries(ticketEntryQuery)

  const latestTickets = useMemo(
    () => getLatestVersions(parseTickets(ticketEntries)),
    [ticketEntries]
  )

  const workflowIds = useMemo(
    () => uniq(latestTickets.map((ticket) => ticket.workflowId)),
    [latestTickets]
  )

  const { libraWorkflows, isLoading: workflowsLoading } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((workflow) => [workflow.id, workflow])),
    [libraWorkflows]
  )

  const tickets = useMemo(
    () =>
      latestTickets.filter((ticket) =>
        Boolean(getNiiReportConfig(workflowMap.get(ticket.workflowId)?.steps))
      ),
    [latestTickets, workflowMap]
  )

  const isLoading =
    queryLoading || ticketPlanLoading || ticketsLoading || workflowsLoading

  return useMemo(
    () => ({
      tickets,
      workflowMap,
      // While workflows load nothing looks eligible yet; reporting a
      // difference then would flash a misleading "not exportable" count.
      ineligibleCount: isLoading ? 0 : latestTickets.length - tickets.length,
      isLoading,
    }),
    [tickets, workflowMap, latestTickets.length, isLoading]
  )
}
